import { getOktaToken, getOktaTokenAsync } from './common';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

type CallbackWithArgs = (args: unknown) => void;

const SUBSCRIBE = 'subscribe';
const CMD = 'CMD';

type Toggles = {
    enableOptionalPbcUi?: boolean;
    enableGenerateContracts?: boolean;
    isDisplayContractStatusEnabled?: boolean;
};

type Selectors = {
    getDealRefId?: () => string;
};

type SocketManagerProps = {
    wsUrl: string;
    messageCallback: CallbackWithArgs;
    dealExchangeId: string; // required for MV to load
    selectors: Selectors;
    toggles?: Toggles;
    onConnectionFailure?: (failed: boolean) => void;
    connectionFailureLogThreshold?: number; // Optional threshold in iterations (minutes), defaults to 10
    healthCheckInterval?: number; // Optional health check interval in seconds
};

export default class SocketManager {
    static KEEP_ALIVE_TIMEOUT = 5 * 60 * 1000;
    static AWS_LIMIT_BUFFER_TIMEOUT = 90 * 60 * 1000; // Recreate connection before hitting AWS 2-hour limit
    static HEALTH_CHECK_INTERVAL = 60; // 60 seconds health check polling
    static MAX_RETRY_ATTEMPTS = 5;
    static RETRY_DELAY_MS = 500;
    static CONNECTION_FAILURE_LOG_THRESHOLD = 10; // 10 iterations (10 minutes) - log as failed if can't connect within this many iterations

    socket: WebSocket | null;
    healthCheckTimer: any | null;
    connectionString: string | null;
    dealExchangeId: string | null;
    messageCallback: CallbackWithArgs;
    websocketUrl: string | null;
    enableOptionalPbcUi: boolean | undefined;
    enableGenerateContracts: boolean;
    isDisplayContractStatusEnabled: boolean | undefined;
    retryCount: number;
    onConnectionFailure?: (failed: boolean) => void;
    connectionOpenedAt: number | null;
    lastKeepAliveAt: number | null;
    totalRecoveryIterations: number;
    connectionFailureLogThreshold: number;
    healthCheckInterval: number;
    hasLoggedFailure: boolean;

    selectors: Selectors = {};

    constructor(props: SocketManagerProps) {
        this.socket = null;
        this.healthCheckTimer = null;
        this.connectionString = null;
        this.dealExchangeId = props.dealExchangeId;
        this.selectors = props.selectors || {};
        this.messageCallback = props.messageCallback;
        this.websocketUrl = props.wsUrl;
        this.enableOptionalPbcUi = props.toggles?.enableOptionalPbcUi || false;
        this.enableGenerateContracts = props.toggles?.enableGenerateContracts || false;
        this.isDisplayContractStatusEnabled = props.toggles?.isDisplayContractStatusEnabled || false;
        this.retryCount = 0;
        this.onConnectionFailure = props.onConnectionFailure;
        this.connectionOpenedAt = null;
        this.lastKeepAliveAt = null;
        this.totalRecoveryIterations = 0;
        this.connectionFailureLogThreshold = props.connectionFailureLogThreshold || SocketManager.CONNECTION_FAILURE_LOG_THRESHOLD;
        this.healthCheckInterval = props.healthCheckInterval || SocketManager.HEALTH_CHECK_INTERVAL;
        this.hasLoggedFailure = false;

        // Explicitly bind the web socket event listeners to the class instance (instead of the web socket)
        this._handleOpen = this._handleOpen.bind(this);
        this._handleMessage = this._handleMessage.bind(this);
        this._handleError = this._handleError.bind(this);
        this._handleClose = this._handleClose.bind(this);
    }

    start(shouldTriggerHealthMonitor = false) {
        this._getConnectionStringAsync().then((connectionString) => {
            this.connectionString = connectionString;
            if (this.connectionString) {
                this.socket = new WebSocket(this.connectionString);

                this.socket.addEventListener('open', this._handleOpen);
                this.socket.addEventListener('message', this._handleMessage);
                this.socket.addEventListener('error', this._handleError);
                this.socket.addEventListener('close', this._handleClose);
            }

            if (shouldTriggerHealthMonitor) {
                this._runHealthMonitor();
            }
        });
    }

    stop() {
        this.retryCount = 0; // Reset retry count when manually stopping
        clearTimeout(this.healthCheckTimer as any);
        this.socket?.close();
    }

    refresh() {
        this._keepAlive();
    }

    updateChangedDealExchangeId(dealExchangeId: string) {
        if (!dealExchangeId || dealExchangeId === this.dealExchangeId) {
            return;
        }
        this.dealExchangeId = dealExchangeId;
        if (this.socket?.readyState === WebSocket.OPEN) {
            this._subscribeToTopics();
        }
    }

    _recreateConnection() {
        this.stop();
        this.start();
    }

    _getWebSocketBaseUrl(): string | undefined {
        // In deployed environments the hostname contains '.com' — route through CloudFront.
        // Otherwise (local dev, CI, etc.) connect directly to the WebSocket backend
        if (window.location.hostname.includes('app.coxautoinc.com')) {
            return `/websockets`;
        }
        return process.env.WEBSOCKETS_URL;
    }

    _getConnectionString() {
        if (window.DealWorkflow?.toggles.get('enableMVCloudFrontWebsocketsProxy') ?? false) {
            const token = getOktaToken() || 'knownorigin';
            return `${this._getWebSocketBaseUrl()}?token=${token}`;
        }
        if (this.websocketUrl) {
            const token = getOktaToken() || 'knownorigin';
            return `wss://${this.websocketUrl}?token=${token}`;
        }
        return null;
    }

    async _getConnectionStringAsync() {
        try {
            if (window.DealWorkflow?.toggles.get('enableMVCloudFrontWebsocketsProxy') ?? false) {
                const token = (await getOktaTokenAsync()) || 'knownorigin';
                return `${this._getWebSocketBaseUrl()}?token=${token}`;
            }
            if (this.websocketUrl) {
                const token = (await getOktaTokenAsync()) || 'knownorigin';
                return `wss://${this.websocketUrl}?token=${token}`;
            }
            return null;
        } catch (err) {
            return null;
        }
    }

    /**
     * NOTE: this function doesn't need "bind" because it is an arrow function.
     */
    _keepAlive = () => {
        this.lastKeepAliveAt = Date.now();
        this._subscribeToTopics();
    };

    /**
     * Health check monitor - runs every minute to check connection status
     * Implements the proposed solution flow:
     * 1. Check if connection is unhealthy (closed/failed) -> recreate
     * 2. Check if connection has been open too long (90 min) -> recreate to avoid AWS 2-hour limit
     * 3. Check if 5+ minutes passed since last keep-alive -> send keep-alive
     */
    _runHealthMonitor = () => {
        this.healthCheckTimer = setTimeout(() => {
            const now = Date.now();

            const isHealthy = this._isConnectionHealthy();
            const shouldRecreate =
                this.connectionOpenedAt && now - this.connectionOpenedAt >= SocketManager.AWS_LIMIT_BUFFER_TIMEOUT;
            const isKeepAliveDue = this.lastKeepAliveAt && now - this.lastKeepAliveAt >= SocketManager.KEEP_ALIVE_TIMEOUT;

            if (!isHealthy) {
                // Check if connection is unhealthy (not open)
                // Check if exceeded configured iteration threshold
                if (this.totalRecoveryIterations >= this.connectionFailureLogThreshold && !this.hasLoggedFailure) {
                    this._logConnectionStatusToNR({
                        meta: {
                            status: 'failed',
                            iteration: this.totalRecoveryIterations
                        }
                    });
                    this.hasLoggedFailure = true;
                }

                this.totalRecoveryIterations++;
                this._recreateConnection();
            } else if (shouldRecreate) {
                // Recreate connection before hitting AWS 2-hour limit (90 min with health monitor)
                this._recreateConnection();
            } else if (isKeepAliveDue) {
                // Check if 5+ minutes passed since last keep-alive
                this._keepAlive();
            }

            // Continue monitoring
            this._runHealthMonitor();
        }, this.healthCheckInterval * 1000); // Convert seconds to milliseconds
    };

    _isConnectionHealthy = () => {
        // Check if connection exists and is open
        return !!(this.socket && this.socket.readyState === WebSocket.OPEN);
    };

    _handleOpen(_event: Event) {
        // If we had retries, we've now successfully reconnected
        if (this.retryCount > 0 || this.totalRecoveryIterations > 0) {
            this._logConnectionStatusToNR({
                meta: {
                    status: 'recovered',
                    iteration: this.totalRecoveryIterations
                }
            });
            this.retryCount = 0;
            this.onConnectionFailure?.(false);
            this.totalRecoveryIterations = 0;
            this.hasLoggedFailure = false;
        }

        // Track connection opened time for health monitoring
        this.connectionOpenedAt = Date.now();
        this.lastKeepAliveAt = Date.now();

        this._subscribeToTopics();
    }

    _subscribeToTopics() {
        const subscribe = (topic: string, data = this.dealExchangeId) => {
            if (this.socket?.readyState !== WebSocket.OPEN) {
                return;
            }
            this.socket.send(JSON.stringify({ action: SUBSCRIBE, topic, data, metadata: CMD }));
        };

        subscribe('dealXgCreate');
        subscribe('dealXgPatch');
        subscribe('pushToFI');

        if (this.enableOptionalPbcUi) {
            subscribe('mv_pushToDms');
        }
        if ((this.enableGenerateContracts || this.isDisplayContractStatusEnabled) && this.selectors.getDealRefId!()) {
            subscribe('drContracting', this.selectors.getDealRefId!());
        }
        subscribe('aioDxgEvents');
        subscribe('mv_docMove');
        subscribe('dealPulsePurchaseHistory');
        subscribe('dealPulseTradeIn');

        const copyCreditAppDealRefId = this.selectors.getDealRefId?.();
        if (copyCreditAppDealRefId) {
            subscribe('copyCreditApp', copyCreditAppDealRefId);
        }
    }

    _handleMessage(event: MessageEvent) {
        let data;

        try {
            data = JSON.parse(event.data);
        } catch (err) {
            data = event.data;
        }

        this.messageCallback(data);
    }

    _handleError(event: Event) {
        // eslint-disable-next-line no-console
        console.warn(event);

        if (this.healthCheckInterval && this.healthCheckInterval <= 30) {
            return;
        }

        // Check if we've exhausted retry attempts
        if (this.retryCount >= SocketManager.MAX_RETRY_ATTEMPTS) {
            this._logConnectionStatusToNR({
                meta: {
                    status: 'retryExhausted',
                    iteration: this.totalRecoveryIterations
                }
            });
            this.onConnectionFailure?.(true);
            return;
        }

        // Increment retry count and retry
        this.retryCount++;

        // Wait before retrying with exponential backoff
        /* istanbul ignore next */
        setTimeout(() => {
            this.start(false);
        }, SocketManager.RETRY_DELAY_MS * this.retryCount);
    }

    _handleClose(_event: CloseEvent) {
        this._logConnectionStatusToNR({
            meta: {
                status: 'closed'
            }
        });
    }

    _logConnectionStatusToNR({ meta = {} }: { meta?: Record<string, string | number> } = {}) {
        const action = {
            dealId: this.dealExchangeId,
            type: 'connectionOutcome',
            retryCount: this.retryCount,
            ...meta
        };
        logNewRelicPageAction('MV:WebSocket', action);
    }
}
